<script>
import MacroList from '@components/macros/macro-list.vue';
import { counterMixin } from '@mixins/counter-mixin';
import { mediaMixin } from '@mixins/media-mixin';
import {
  authComputed,
  platelistComputed,
  platelistMethods,
  recipeComputed,
} from '@state/helpers';

export default {
  components: {
    MacroList,
  },
  props: {
    recipe: {
      type: Object,
      required: true,
      default: null,
    },
    selectable: {
      type: Boolean,
      required: false,
    },
    isDragging: {
      type: Boolean,
      required: false,
    },
    isLibraryRecipe: {
      type: Boolean,
      required: false,
    },
    isLeftover: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [counterMixin, mediaMixin],
  data() {
    return {
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
    };
  },
  computed: {
    ...authComputed,
    ...platelistComputed,
    ...recipeComputed,
    isSelected() {
      return (
        this.recipe &&
        this.selectedMealtimeDish &&
        this.recipe.id === this.selectedMealtimeDish.id
      );
    },
    formatTitle() {
      const maxChars = 20;
      const substrLength = this.isLibraryRecipe ? 18 : 20;
      const title = this.recipe.title;
      const truncate = title.length > maxChars;
      return truncate
        ? this.recipe.title.substr(0, substrLength) + '...'
        : title;
    },
    hasPrepareTime() {
      return (
        this.recipe &&
        this.recipe.preparetime &&
        !this.recipe.preparetime.includes('null')
      );
    },
    prepTime() {
      return this.$_formatPrepTime(this.recipe.totaltime);
    },
    coverPhoto() {
      if (this.recipe && (this.recipe.imageurl || this.recipe.imageURL)) {
        const imgUrl =
          this.recipe.imageurl || this.recipe.imageURL || this.recipe.imageUr;
        return this.$_transformCloudinaryImg(imgUrl);
      } else {
        return this.placeholder;
      }
    },
    isPlatelistLocked() {
      return (
        this.selectedPlatelist &&
        this.selectedPlatelist.editabilityStatus &&
        this.selectedPlatelist.editabilityStatus.locked
      );
    },
    isRecipePublished() {
      return this.recipe && !this.recipe.draft && !this.recipe.archived;
    },
  },
  methods: {
    ...platelistMethods,
    toRecipeDetail() {
      this.$router
        .push({
          name: 'recipeDetail',
          params: { id: this.recipe.id, recipe: this.recipe },
        })
        .catch(() => {});
    },
    viewLibraryRecipe() {
      this.$emit('view-recipe-details', this.recipe);
    },
    selectLibraryRecipe() {
      if (!this.isLibraryRecipe) {
        this.toRecipeDetail();
      } else if (!this.isPlatelistLocked && this.isRecipePublished) {
        if (this.isAddingDish) {
          this.setIsAddingDish(false);
          this.setSelectedMealtimeDish(null);
        } else {
          this.$emit('select-recipe', this.recipe);
        }
      }
    },
  },
};
</script>

<template>
  <div
    :class="[
      'card-recipe-container',
      {
        dragging: isDragging,
        'in-library': isLibraryRecipe,
        'is-selected': isSelected,
      },
    ]"
  >
    <div
      :class="[
        'card-recipe-minimal',
        {
          selectable: selectable,
          'archived-overlay': recipe.archived && showRecipeArchivedOverlay,
        },
      ]"
      @click="selectLibraryRecipe"
    >
      <div class="card-hero-wrapper">
        <div class="card-hero" v-lazy:background-image="coverPhoto">
          <div class="card-meta">
            <div
              :class="[
                'card-meta-top',
                { 'without-preptime': !hasPrepareTime },
              ]"
            >
              <div class="recipe-cook-time" v-if="hasPrepareTime">
                <svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-stopwatch'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 0h6v2H6V0zm2 13V7h2v6H8zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 009 3a9 9 0 00-9 9c0 4.97 4.02 9 9 9a8.994 8.994 0 007.03-14.61zM2 12c0 3.87 3.13 7 7 7s7-3.13 7-7-3.13-7-7-7-7 3.13-7 7z" fill="#000"/></svg>
                {{ prepTime }}
              </div>
              <div
                :class="[
                  'recipe-status',
                  { draft: recipe.draft, archived: recipe.archived },
                ]"
                v-if="recipe.draft || recipe.archived"
              >
                <span v-if="recipe.draft">Draft</span>
                <span v-if="recipe.archived">Archived</span>
              </div>
            </div>
            <div class="recipe-flagged" v-if="recipe.flagged">
              Flagged
            </div>
          </div>
        </div>
      </div>
      <div class="card-recipe-content">
        <h3><span v-if="isLeftover">Leftover</span> {{ formatTitle }}</h3>
        <MacroList v-if="recipe" :recipe="recipe" :showCalories="true" />
      </div>
      <div class="library-actions" v-if="isLibraryRecipe">
        <span class="link library-action" @click="viewLibraryRecipe"
          >View Details</span
        >
        <span
          v-if="!isPlatelistLocked && isRecipePublished"
          class="link library-action"
          @click="selectLibraryRecipe"
          >Select</span
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.card-recipe-container {
  margin: 0 0 3rem 0;
  padding: 0 0.8rem 0;
  position: relative;

  &.sortable-drag {
    .card-hero {
      max-height: 2.5rem;
      width: 2.5rem;
    }

    .card-meta,
    .card-recipe-content {
      display: none;
    }
  }

  .card-recipe-minimal {
    border: 10px solid white;
    border-radius: $base-border-radius-md;
    position: relative;
    transition: transform $duration-animation-base - 75 ease-out,
      box-shadow $duration-animation-base - 50 ease-out;

    &.archived-overlay {
      .recipe-status {
        position: relative;
        z-index: 1;
      }

      &::after {
        background: rgba($color-white-wo-rgb, 0.5);
        border-radius: $base-border-radius-md;
        bottom: 0;
        content: '';
        left: 0;
        position: absolute;
        right: 0;
        top: 0;
      }
    }

    &:hover {
      box-shadow: 0 0.4rem 0.8rem rgba($color-black-wo-rgb, 0.1);
      cursor: pointer;
      transform: scale(1.01);
    }

    .card-hero-wrapper {
      background-color: white;
    }

    .card-hero {
      align-items: flex-start;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
      border-radius: $base-border-radius-md;
      display: flex;
      height: 17rem;
      justify-content: space-between;
      position: relative;

      @include media('xxxl', null) {
        height: 20rem;
      }

      .card-hero-checkbox {
        left: 1.3rem;
        position: relative;
        top: 1.3rem;
      }

      .card-meta {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
        padding: 1rem;
        width: 100%;

        .card-meta-top {
          display: flex;
          justify-content: space-between;
          width: 100%;

          &.without-preptime {
            flex-direction: row-reverse;
          }
        }

        .recipe-cook-time,
        .recipe-status,
        .recipe-flagged {
          align-items: center;
          border-radius: 0.5rem;
          color: white;
          display: flex;
          font-size: 1.2rem;
          font-weight: 800;
          height: 2.5rem;
          letter-spacing: 0.1px;
          line-height: 1.6rem;
          padding: 0.3rem 0.9rem;
          text-transform: uppercase;
        }

        .recipe-cook-time {
          background: $color-card-program-meta-bg;
          mix-blend-mode: normal;
          opacity: 0.7;
          width: 8.7rem;

          .icon-stopwatch {
            height: 1.3rem;
            margin-right: 0.3rem;
            width: 1.3rem;

            path {
              fill: white;
            }
          }
        }

        .recipe-status {
          &.draft {
            background-color: $color-action;
          }

          &.archived {
            background-color: black;
          }
        }

        .recipe-flagged {
          background-color: $color-red;
          display: flex;
          justify-content: center;
        }
      }
    }

    .card-recipe-content {
      background-color: white;
      padding-bottom: 0.1rem;
      padding-top: 1.3rem;
    }

    &.selectable {
      .card-hero .card-meta {
        left: unset;
        right: 1.4rem;
      }
    }
  }

  &.sortable-ghost {
    opacity: 0.3;
  }

  &.in-library {
    .library-actions {
      display: flex;
      justify-content: space-between;
      padding: 0 1rem 0.8rem;

      .library-action {
        color: black;
        font-size: 1.4rem;
        font-weight: 800;
        letter-spacing: 0.116667px;
        text-decoration: none;
      }
    }

    &:hover {
      cursor: default;
      padding: 0 0.125rem;

      .library-actions {
        padding: 0 1.1rem 0.8rem;
      }
    }

    &.is-selected {
      border: 2px solid $color-primary;
      border-radius: $base-border-radius-md;

      &:hover {
        box-shadow: none !important;
        cursor: default !important;
        padding: 0 0.125rem !important;

        .library-actions {
          padding: 0 1rem 0.8rem;

          .link.library-action:last-child {
            cursor: default;
          }
        }
      }

      .card-recipe-minimal:hover {
        box-shadow: none;
        cursor: default;
        transform: unset;
      }
    }
  }
}
</style>
